import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, Send, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { contact } from "../mock";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

export default function Contact() {
  const { toast } = useToast();
  const location = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [quizResult, setQuizResult] = useState(null);
  const [requested, setRequested] = useState(null);
  const [state, setState] = useState("idle"); // idle | sending | sent

  useEffect(() => {
    if (location.state?.quizResult) {
      setQuizResult(location.state.quizResult);
      setForm((f) => ({ ...f, message: `My quiz suggested: ${location.state.quizResult}. I'd like to discuss my options.` }));
    }
    if (location.state?.requestedCounsellor) {
      setRequested(location.state.requestedCounsellor);
      setForm((f) => ({ ...f, message: `I would like to request a session with ${location.state.requestedCounsellor}.` }));
    }
  }, [location.state]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/appointments", { ...form, quiz_result: quizResult });
      setState("sent");
      toast({ title: "Request received!", description: "We'll reach out to schedule your free session." });
      setForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setState("idle"), 4000);
    } catch (err) {
      setState("idle");
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-[#16244F] overflow-hidden">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#2b8cf0]/20 blur-2xl" />
      <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#F58220]/20 blur-2xl" />

      <div className="relative max-w-[1240px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="text-white">
          <h2 className="font-head text-3xl md:text-4xl font-bold leading-tight">
            Get a <span className="text-[#F9B57C]">FREE</span> professional Career Counselling today!
          </h2>
          <p className="mt-5 text-white/80 text-lg">
            Call us or send us a message to book an appointment. Take the first confident step toward your future.
          </p>
          <div className="mt-8 space-y-4">
            <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-[#F9B57C] group-hover:bg-[#F58220] group-hover:text-white transition-colors">
                <Phone size={20} />
              </span>
              <span className="font-head text-lg font-semibold">{contact.phone}</span>
            </a>
            <a href={`mailto:${contact.email}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-[#F9B57C] group-hover:bg-[#F58220] group-hover:text-white transition-colors">
                <Mail size={20} />
              </span>
              <span className="font-head text-lg font-semibold break-all">{contact.email}</span>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl p-7 md:p-9 shadow-2xl">
          <h3 className="font-head text-xl font-bold text-[#16244F] mb-5">Book an Appointment</h3>
          {quizResult && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-[#eaf3ff] text-[#2b8cf0] rounded-lg px-3 py-2 font-semibold">
              <Sparkles size={15} /> Based on your quiz: {quizResult}
            </div>
          )}
          {requested && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-[#F58220]/10 text-[#F58220] rounded-lg px-3 py-2 font-semibold">
              <Sparkles size={15} /> Requesting counsellor: {requested}
            </div>
          )}
          <div className="space-y-4">
            <input name="name" value={form.name} onChange={onChange} required placeholder="Full name"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="Email"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
            </div>
            <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Tell us about your goals..."
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition resize-none" />
            <button type="submit" disabled={state !== "idle"}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-80">
              {state === "sending" ? (<><Loader2 size={18} className="animate-spin" /> Sending...</>)
                : state === "sent" ? (<><CheckCircle2 size={18} /> Request Sent</>)
                : (<><Send size={18} /> Send Request</>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
